import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Heart, Zap, Moon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface QuizQuestion {
  id: number;
  question: string;
  options: {
    label: string;
    icon: React.ReactNode;
    value: string;
  }[];
}

const questions: QuizQuestion[] = [
  {
    id: 1,
    question: "What is your main goal?",
    options: [
      { label: "Reduce daily stress", icon: <Heart className="h-6 w-6" />, value: "stress" },
      { label: "Improve concentration", icon: <Zap className="h-6 w-6" />, value: "focus" },
      { label: "Better sleep", icon: <Moon className="h-6 w-6" />, value: "sleep" },
      { label: "Overall wellness ritual", icon: <Sparkles className="h-6 w-6" />, value: "global" },
    ],
  },
  {
    id: 2,
    question: "When do you feel the most tension?",
    options: [
      { label: "At work / in meetings", icon: <Zap className="h-6 w-6" />, value: "work" },
      { label: "In the evening at home", icon: <Moon className="h-6 w-6" />, value: "evening" },
      { label: "During commutes", icon: <RefreshCw className="h-6 w-6" />, value: "commute" },
      { label: "Throughout the day", icon: <Heart className="h-6 w-6" />, value: "allday" },
    ],
  },
  {
    id: 3,
    question: "Which style suits you best?",
    options: [
      { label: "Discreet and elegant", icon: <Sparkles className="h-6 w-6" />, value: "elegant" },
      { label: "Modern and bold", icon: <Zap className="h-6 w-6" />, value: "modern" },
      { label: "Natural and authentic", icon: <Heart className="h-6 w-6" />, value: "natural" },
      { label: "Versatile and practical", icon: <RefreshCw className="h-6 w-6" />, value: "versatile" },
    ],
  },
];

interface ProductRecommendation {
  title: string;
  description: string;
  handle: string;
  isCollection: boolean;
}

// Map quiz answers to actual Shopify products (handles include ™ symbol)
const getRecommendation = (answers: string[]): ProductRecommendation => {
  const [objective, timing, style] = answers;

  // Sleep/Evening → Calmi Set (complete wellness)
  if (objective === "sleep" || timing === "evening") {
    return {
      title: "Calmi Set™",
      description: "The perfect set to calm the mind and promote restful sleep. Magnetic copper bracelet and ring for a complete wellness ritual.",
      handle: "calmi-set™-adjustable-twisted-copper-magnetic-bracelet-and-ring-for-everyday-balance",
      isCollection: false,
    };
  }

  // Focus/Work → Calmi Ring (discrete, professional)
  if (objective === "focus" || timing === "work") {
    return {
      title: "Calmi Ring™",
      description: "Rotating ring ideal for maintaining focus at work. The discreet motion helps channel your mental energy.",
      handle: "calmi-ring™-adjustable-copper-magnetic-ring-for-everyday-balance",
      isCollection: false,
    };
  }

  // Global wellness/All day → Chakra Bracelet (holistic)
  if (objective === "global" || timing === "allday") {
    return {
      title: "Calmi Chakra™",
      description: "7 chakra bracelet for energy balance throughout the day. Natural crystals to harmonize body and mind.",
      handle: "calmi-chakra™-adjustable-7-chakra-crystal-bracelet-for-meditation-mindful-balance",
      isCollection: false,
    };
  }

  // Elegant/Natural style → Calmi Tree Bracelet
  if (style === "elegant" || style === "natural") {
    return {
      title: "Calmi Tree™",
      description: "Tree of Life magnetic copper bracelet. Natural and elegant design for authentic daily wellness.",
      handle: "calmi-tree™-tree-of-life-copper-magnetic-cuff-bracelet-for-men-and-women",
      isCollection: false,
    };
  }

  // Modern/Versatile → Calmi Chakra (Smart Ring is draft/unpublished)
  if (style === "modern" || style === "versatile") {
    return {
      title: "Calmi Chakra™",
      description: "Modern bracelet with natural stones for daily balance. Contemporary and practical style for all occasions.",
      handle: "calmi-chakra™-adjustable-7-chakra-crystal-bracelet-for-meditation-mindful-balance",
      isCollection: false,
    };
  }

  // Default → Calmi Ring (best seller)
  return {
    title: "Calmi Ring™",
    description: "Our best-seller: 99% pure copper rotating ring with integrated magnets. Discreet and elegant wellness for your daily life.",
    handle: "calmi-ring™-adjustable-copper-magnetic-ring-for-everyday-balance",
    isCollection: false,
  };
};

export const ProductQuiz = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (value: string) => {
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
  };

  const recommendation = showResult ? getRecommendation(answers) : null;

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  if (!isOpen) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-primary/10 to-secondary rounded-2xl p-5 md:p-8 text-center"
      >
        <Sparkles className="h-8 md:h-10 w-8 md:w-10 mx-auto mb-4 text-primary" />
        <h3 className="font-display text-xl md:text-3xl mb-3">
          Which product is right for you?
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm md:text-base">
          Answer 3 questions and discover the perfect anti-stress accessory for your daily life.
        </p>
        <Button
          onClick={() => setIsOpen(true)}
          size="lg"
          className="gap-2"
        >
          Start the Quiz
          <ArrowRight className="h-4 w-4" />
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-background border border-border rounded-2xl p-5 md:p-8 shadow-xl"
    >
      {/* Progress Bar */}
      <div className="mb-6 md:mb-8">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>Question {showResult ? questions.length : currentQuestion + 1} / {questions.length}</span>
          <span>{Math.round(((showResult ? questions.length : currentQuestion + 1) / questions.length) * 100)}%</span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${((showResult ? questions.length : currentQuestion + 1) / questions.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait" custom={1}>
        {showResult && recommendation ? (
          <motion.div
            key="result"
            initial="enter"
            animate="center"
            exit="exit"
            variants={slideVariants}
            custom={1}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            <div className="w-14 md:w-16 h-14 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-7 md:h-8 w-7 md:w-8 text-primary" />
            </div>
            <h3 className="font-display text-xl md:text-3xl mb-2">
              Your Recommendation
            </h3>
            <p className="text-primary font-semibold text-lg md:text-xl mb-4">
              {recommendation.title}
            </p>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto text-sm md:text-base">
              {recommendation.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/shop/${recommendation.handle}`}
                className="btn-calim-primary text-sm h-12 min-w-[180px] inline-flex items-center justify-center"
              >
                Discover the Product
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Button
                variant="outline"
                onClick={handleReset}
                className="gap-2 h-12 min-w-[180px]"
              >
                <RefreshCw className="h-4 w-4" />
                Retake Quiz
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={currentQuestion}
            initial="enter"
            animate="center"
            exit="exit"
            variants={slideVariants}
            custom={1}
            transition={{ duration: 0.3 }}
          >
            <h3 className="font-display text-xl md:text-3xl text-center mb-6 md:mb-8">
              {questions[currentQuestion].question}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
              {questions[currentQuestion].options.map((option, index) => (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleAnswer(option.value)}
                  className="flex items-center gap-3 md:gap-4 p-3 md:p-4 border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
                >
                  <div className="w-10 md:w-12 h-10 md:h-12 flex-shrink-0 bg-secondary rounded-full flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                    {option.icon}
                  </div>
                  <span className="font-medium text-sm md:text-base">{option.label}</span>
                </motion.button>
              ))}
            </div>

            {currentQuestion > 0 && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Previous Question
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
